import { provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import {
  init,
  browserTracingIntegration,
  replayIntegration,
  replayCanvasIntegration,
  type Breadcrumb,
  type ErrorEvent as SentryErrorEvent,
} from '@sentry/angular';
import { scrubPii } from '@bizzdesign/shared/utils';
import { environment } from './environments/environment';

const SENTRY_DSN =
  'https://12581b388c78a96d0769c0aacca48965@o245897.ingest.us.sentry.io/4507487772016640';
// Sentry only reports for production, or locally when opted in via
// `enableSentryLocally` in your personal environment.development.ts (see Environment type).
// e2e environments are excluded so test runs do not pollute Sentry with noise.
const sentryEnabled =
  (environment.production || environment.enableSentryLocally) &&
  environment.type !== 'e2e';
init({
  dsn: sentryEnabled ? SENTRY_DSN : undefined,
  environment: environment.type || 'production',
  integrations: [
    browserTracingIntegration(),
    replayIntegration({
      blockAllMedia: true, // explicit default — keeps real media (screenshots, video) blocked
      unblock: ['svg', '[data-role="image-overlay"]'], // reveals the modeler's SVG diagram canvas and pasted images instead of a blank blocked box
      // Scoped to the diagram container only — do not widen to an app-shell selector
      // (e.g. `bd-layout`), which would unmask all routed content, not just the canvas.
      unmask: ['#graphComponent'],
    }),
    // yFiles renders its grid background via <canvas> (RenderMode.CANVAS in
    // EditorGraph.ts); without this integration Session Replay leaves it blank.
    replayCanvasIntegration({ quality: 'high' }),
  ],
  beforeSend: (event) => scrubPii(event) as SentryErrorEvent,
  beforeBreadcrumb: (breadcrumb) => scrubPii(breadcrumb) as Breadcrumb,

  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions

  // Session Replay
  replaysSessionSampleRate: environment.production ? 0.1 : 1.0, // 10% sampling in production; full capture when testing locally so the one test session is actually recorded
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

bootstrapApplication(AppComponent, {
  ...appConfig,
  providers: [provideZoneChangeDetection(), ...appConfig.providers],
}).catch((err) => console.error(err));
